// ===========================================================================
// Regulars, client side: the bell, the notice toast, and the panel.
//
// A regular is someone you have both waved at after a chat. You stay
// anonymous to each other: the panel shows the handle you saw them use, when
// you became regulars, and -- only when they are in a room you could walk
// into yourself -- which room. Never a "last seen", never a status line,
// never anything they did not do in front of you.
//
// The panel is a modal in the shape of the account panel. It is rendered
// from whatever REGULARS the server last sent; app.js hands it each new one
// and it redraws. With no socket (opened from the landing) it explains itself
// and waits. The copy says exactly what a wave and a block do, because the
// honest version is the whole point of the feature. ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { asModal } from './modal.js';
import { el } from '../dom.js';
import { createCallButton } from './call-sheets.js'; // calls (5.B4)
import { createThreadView } from './messages.js';

const BELL_SVG =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>';

/** The copy under the Wave control. One sentence of what it is, one of what it is not. */
export const WAVE_COPY = 'Wave if you would like to see them again. A regular is someone you have both waved at; you stay anonymous to each other.';
/** What a person is told when the other side had no identity to remember them by. */
export const NO_IDENTITY_COPY = 'They are not remembering anyone today.';
/**
 * How long the Wave control stays next to the searching status.
 *
 * Twenty seconds, and it was missed: a chat that had run for two hours ended
 * and the one chance to say "I would like to see them again" was gone before
 * the person had finished reading it. The whole regulars feature hangs off
 * this one button, and the cost of leaving it up longer is a button nobody
 * presses. Ninety seconds, and it no longer vanishes while being read --
 * clearWave now only runs on the next match or an explicit dismissal.
 */
export const WAVE_OFFER_MS = 90_000;
/** The hours a quiet-hours window can start or end on. */
export const HOURS = Array.from({ length: 24 }, (_, i) => i);
/** What the pickers show before anything is chosen: a night, which is what
 *  almost everyone means by quiet hours. */
export const DEFAULT_QUIET = Object.freeze({ from: 22, to: 7 });

/**
 * "regular since <date>" -- a calendar date, never a time: the moment is not
 * the point, and a timestamp would read as surveillance of a friendship.
 * @param {string} iso
 * @param {number} [nowMs]  for tests
 */
export function formatSince(iso, nowMs = Date.now()) {
  const ms = Date.parse(iso || '');
  if (!Number.isFinite(ms)) return 'regular';
  const days = Math.floor((nowMs - ms) / 86_400_000);
  if (days <= 0) return 'regular since today';
  if (days === 1) return 'regular since yesterday';
  if (days < 7) return `regular since ${days} days ago`;
  const d = new Date(ms);
  const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  return `regular since ${month} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/**
 * One line of status for a regular's row: where they are if you could join,
 * else whether they are around at all. Invisible people arrive here as
 * neither, which is the whole of what invisible means.
 * @param {{ connected?: boolean, around?: { name?: string }|null }} r
 * @returns {{ text: string, kind: 'room'|'around'|'none' }}
 */
export function regularStatus(r) {
  if (r && r.around) return { text: `in ${r.around.name || 'a room'}`, kind: 'room' };
  if (r && r.connected) return { text: 'around', kind: 'around' };
  return { text: '', kind: 'none' };
}

/**
 * What a WAVED answer means on screen. `null` means show nothing.
 * @param {{ ok: boolean, reason?: string }} m
 */
export function waveResultText(m) {
  if (!m) return null;
  if (m.ok) return 'Waved.';
  switch (m.reason) {
    case 'no_identity':
      return NO_IDENTITY_COPY;
    case 'expired':
      return 'That chat was a while ago.';
    case 'rate_limited':
      return 'Slow down a moment.';
    default:
      return null; // unknown_pair, not_available, bad_request: nothing to say
  }
}

/**
 * The bell: a button with an unread dot. One per screen that has a header
 * (the 1:1 chat, the rooms hub); app.js keeps them in step.
 * @param {{ onOpen: () => void }} h
 * @returns {{ root: HTMLElement, api: { setUnread: (n: number) => void } }}
 */
export function createBell({ onOpen }) {
  const dot = el('span', { class: 'bell-dot', 'aria-hidden': 'true' });
  const root = el('button', {
    type: 'button',
    class: 'btn btn-icon btn-ghost bell',
    title: t('Your regulars'),
    'aria-label': 'Your regulars',
    onClick: () => onOpen?.(),
  }, [el('span', { html: BELL_SVG }), dot]);
  return {
    root,
    api: {
      setUnread(n) {
        const on = Number(n) > 0;
        root.classList.toggle('has-unread', on);
        root.setAttribute('aria-label', on ? `Your regulars, ${n} new` : 'Your regulars');
      },
    },
  };
}

let noticeTimer = null;
/**
 * A toast with room for one action. dom.js's toast is text only, and a notice
 * that a regular is in a room is worth a Join button -- so this is its own
 * element, styled like the toast, never stacking (a new one replaces the last).
 *
 * The action is offered whenever the caller passes one. It used to be gated on
 * `notice.roomId` as well, which quietly dropped the button from every notice
 * carrying only a `code`: a community session about to start said so and gave
 * no way to go, which is the one notice where going is the whole point.
 *
 * @param {{ text: string, roomId?: string, code?: string }} notice
 * @param {{ onAction?: (n: object) => void, actionLabel?: string, ms?: number }} [opts]
 *   `actionLabel` because the action is not always joining: the owner of a
 *   community being told somebody asked to join needs the panel where they can
 *   say yes, not the room.
 */
export function showNoticeToast(notice, { onAction = null, actionLabel = 'Join', ms = 6000 } = {}) {
  document.querySelector('.notice-toast')?.remove();
  const kids = [el('span', { class: 'notice-text', text: String(notice.text || '') })];
  if (typeof onAction === 'function') {
    kids.push(
      el('button', {
        type: 'button',
        class: 'btn btn-primary btn-xs',
        text: actionLabel,
        onClick: () => {
          node.remove();
          onAction(notice);
        },
      }),
    );
  }
  const node = el('div', { class: 'toast notice-toast', role: 'status' }, kids);
  document.body.append(node);
  void node.offsetWidth;
  node.classList.add('show');
  clearTimeout(noticeTimer);
  noticeTimer = setTimeout(() => node.remove(), ms);
  return node;
}

/**
 * Open the regulars panel.
 * @param {object} h
 * @param {{ regulars: Array<object>, invisible: boolean, quietHours?: object|null }} h.state  the last REGULARS, or an empty one
 * @param {boolean} h.connected  whether there is a socket to act through
 * @param {number} [h.count]     the landing's count, when there is no list yet
 * @param {(personId: string) => void} [h.onBlock]
 * @param {(personId: string, mode: 'video'|'audio', handle: string|null) => void} [h.onCall]  calls (5.B4); absent hides the button
 * @param {boolean} [h.canVideo]  whether this band may ask for video (the chooser offers Voice only when not)
 * @param {(around: { roomId: string, code?: string }) => void} [h.onJoin]
 * @param {(invisible: boolean) => void} [h.onInvisible]
 * @param {(quietHours: { from: number, to: number, offset: number }|null) => void} [h.onQuietHours]
 *   absent hides the control entirely
 * @param {() => void} [h.onRefresh]
 * @param {() => void} [h.onClose]
 * @param {{ list: Function, send: Function, remove: Function, report: Function, newId: Function }|null} [h.messages]
 *   the socket calls for messages between regulars (ui/messages.js); null when the feature is off
 * @returns {{ modal: HTMLElement, api: { update: (state: object) => void, close: () => void, messages: object|null } }}
 */
export function openRegulars(h) {
  let state = h.state || { regulars: [], invisible: false, recent: [] };
  // The list and the thread are two children of the body, so a redraw of the
  // list (every REGULARS) never disturbs a thread somebody is reading.
  const listWrap = el('div', { class: 'regulars-list-wrap' });
  const body = el('div', { class: 'billing-body regulars-body' }, [listWrap]);
  const modal = el('div', { class: 'billing-modal account-modal regulars-modal', role: 'dialog', 'aria-label': 'Your regulars' }, [
    el('div', { class: 'billing-head' }, [
      el('strong', { text: t('Your regulars') }),
      el('button', { class: 'btn btn-ghost btn-sm', text: t('Close'), onClick: () => close() }),
    ]),
    body,
    el('div', {
      class: 'billing-foot muted',
      text: t('A regular is someone you have both waved at after a chat. Lumi keeps the two of you, the names you saw, the date, and any messages you send each other for thirty days. Nothing else: no history, and never where anyone was.'),
    }),
  ]);

  // ---- messages (ui/messages.js): the thread view, mounted once ----
  const thread = h.messages
    ? createThreadView({
        onBack: () => {
          listWrap.hidden = false;
          h.onRefresh?.(); // the badges may have changed while reading
        },
        onList: (withPersonId, before) => h.messages.list(withPersonId, before),
        onSend: (to, text, id, slow) => h.messages.send(to, text, id, slow),
        onDelete: (id) => h.messages.remove(id),
        onReport: (id, reason) => h.messages.report(id, reason),
        newId: h.messages.newId,
      })
    : null;
  if (thread) body.append(thread.root);
  function openThread(r) {
    if (!thread) return;
    listWrap.hidden = true;
    thread.api.open(r);
  }
  // ---- end messages ----

  function close() {
    thread?.api.close();
    dialog.close();
    h.onClose?.();
  }

  function invisibleSwitch() {
    const btn = el(
      'button',
      {
        type: 'button',
        class: 'timed-toggle invisible-toggle',
        role: 'switch',
        aria: { checked: state.invisible ? 'true' : 'false' },
        disabled: h.connected ? null : '',
        onClick: () => {
          const next = !state.invisible;
          state = { ...state, invisible: next }; // optimistic; the server's REGULARS confirms
          btn.setAttribute('aria-checked', next ? 'true' : 'false');
          h.onInvisible?.(next);
        },
      },
      [
        el('span', { class: 'timed-box', 'aria-hidden': 'true' }),
        el('span', { class: 'timed-label' }, [
          el('strong', { text: t('Invisible') }),
          el('span', { class: 'timed-hint', text: t('Your regulars will not see which room you are in, or that you are here at all.') }),
        ]),
      ],
    );
    return btn;
  }

  /**
   * Quiet hours: the window in which nothing rings. The server has gated every
   * notice on this from the start and had no way to be told about it -- there
   * was no screen for it anywhere -- so it was a setting nobody could set.
   *
   * Hours on the PERSON'S clock, plus their offset, because the server cannot
   * guess a timezone and should not try. `from === to` is how the server spells
   * "none", so the switch sends null rather than a zero-length window.
   */
  function quietHoursControl() {
    const q = state.quietHours || null;
    const hourSelect = (name, value) =>
      el(
        'select',
        {
          class: 'quiet-hour',
          name,
          'aria-label': name === 'from' ? 'Quiet from' : 'Quiet until',
          disabled: q && h.connected ? null : '',
          onChange: () => send(),
        },
        HOURS.map((hh) =>
          el('option', { value: String(hh), text: `${String(hh).padStart(2, '0')}:00`, selected: hh === value ? '' : null }),
        ),
      );
    const fromSel = hourSelect('from', q ? q.from : DEFAULT_QUIET.from);
    const toSel = hourSelect('to', q ? q.to : DEFAULT_QUIET.to);
    const summary = el('span', { class: 'timed-hint' });

  
  function draw() {
      const on = !!state.quietHours;
      toggle.setAttribute('aria-checked', on ? 'true' : 'false');
      fromSel.disabled = !on || !h.connected;
      toSel.disabled = !on || !h.connected;
      summary.textContent = on
        ? 'Nothing will ring in this window. Messages and requests still arrive; you just find them yourself.'
        : 'Pick a window in which nothing rings. Off by default.';
    }

    function send() {
      if (!state.quietHours) return;
      const from = Number(fromSel.value);
      const to = Number(toSel.value);
      // The server reads from === to as "no quiet hours", which would silently
      // turn the switch off under the person. Say so instead.
      if (from === to) {
        summary.textContent = 'Pick two different hours: a window that starts and ends at the same time is not a window.';
        return;
      }
      const next = { from, to, offset: -new Date().getTimezoneOffset() };
      state = { ...state, quietHours: next }; // optimistic; REGULARS confirms
      h.onQuietHours?.(next);
    }

    const toggle = el(
      'button',
      {
        type: 'button',
        class: 'timed-toggle quiet-toggle',
        role: 'switch',
        aria: { checked: state.quietHours ? 'true' : 'false' },
        disabled: h.connected ? null : '',
        onClick: () => {
          if (state.quietHours) {
            state = { ...state, quietHours: null };
            draw();
            h.onQuietHours?.(null);
            return;
          }
          const next = { from: Number(fromSel.value), to: Number(toSel.value), offset: -new Date().getTimezoneOffset() };
          state = { ...state, quietHours: next };
          draw();
          h.onQuietHours?.(next);
        },
      },
      [
        el('span', { class: 'timed-box', 'aria-hidden': 'true' }),
        el('span', { class: 'timed-label' }, [el('strong', { text: t('Quiet hours') }), summary]),
      ],
    );
    draw();
    return el('div', { class: 'quiet-hours' }, [
      toggle,
      el('div', { class: 'quiet-range' }, [
        el('span', { class: 'muted', text: t('From') }),
        fromSel,
        el('span', { class: 'muted', text: 'until' }),
        toSel,
      ]),
    ]);
  }

  /**
   * People you talked to this week, and a Wave for each.
   *
   * The site has always said a wave waits seven days for the other one. For
   * the person doing the waving that was not true: the only button that sent
   * one lived for twenty seconds beside a spinner, and a two-hour conversation
   * could end with no way to say you would like another. This is the promise
   * kept -- the same wave, the same window, from a list.
   *
   * It never says who waved at YOU. An unanswered wave is private; you learn
   * of one only by sending your own and finding it was mutual.
   */
  function recentChats() {
    const list = (Array.isArray(state.recent) ? state.recent : []).filter(Boolean);
    if (!h.connected || typeof h.onWavePerson !== 'function' || !list.length) return el('div', { hidden: '' });
    const rows = list.map((r) => {
      const name = r.handle || t('Someone you talked to');
      const btn = el('button', {
        class: `btn btn-sm ${r.waved ? 'btn-ghost' : 'btn-secondary'} recent-wave`,
        type: 'button',
        disabled: r.waved ? '' : undefined,
        text: r.waved ? t('Waved') : t('Wave'),
        title: r.waved
          ? t('You have waved. If they wave back within seven days you become regulars.')
          : t('Wave at them. If they wave back within seven days you become regulars.'),
        onClick: () => {
          btn.disabled = true;
          btn.textContent = t('Waving...');
          h.onWavePerson(r.personId);
        },
      });
      return el('div', { class: 'recent-row' }, [
        el('span', { class: 'recent-name', text: name }),
        el('span', { class: 'recent-when muted', text: whenLeft(r.expiresAt) }),
        btn,
      ]);
    });
    return el('div', { class: 'recent-chats' }, [
      el('p', { class: 'recent-title', text: t('People you talked to this week') }),
      el('p', { class: 'muted recent-note', text: t('You can wave for seven days after a chat. If they wave too, you become regulars. Nobody is told about a wave that was not returned.') }),
      el('div', { class: 'recent-list' }, rows),
    ]);
  }

  /** "6 days left", in whole days, because a countdown is not the point. */
  function whenLeft(expiresAt) {
    const ms = Date.parse(expiresAt || '') - Date.now();
    if (!Number.isFinite(ms) || ms <= 0) return '';
    const days = Math.ceil(ms / (24 * 60 * 60 * 1000));
    return days <= 1 ? t('today') : t('{n} days left', { n: days });
  }

  function row(r) {
    const status = regularStatus(r);
    const actions = [];
    // ---- messages (ui/messages.js): a Message button with the unread badge ----
    if (h.connected && thread) {
      const unread = Number(r.unread) || 0;
      actions.push(
        el('button', {
          type: 'button',
          class: `btn btn-secondary btn-sm regular-message${unread ? ' has-unread' : ''}`,
          title: unread ? `${unread} new message${unread === 1 ? '' : 's'}` : 'Send them a message',
          'aria-label': unread ? `Message, ${unread} new` : 'Message',
          onClick: () => openThread(r),
        }, ['Message', unread ? el('span', { class: 'regular-unread', text: String(unread) }) : null]),
      );
    }
    // ---- end messages ----
    if (r.around && h.connected && typeof h.onJoin === 'function') {
      actions.push(el('button', { type: 'button', class: 'btn btn-secondary btn-sm', text: t('Join'), onClick: () => { h.onJoin(r.around); close(); } }));
    }
    // ---- Calls (5.B4): a Call button, lit only while they are connected. ----
    // The panel closes when a call is placed so the "Calling..." sheet has
    // the screen; the rules live in calls.js on the server.
    if (h.connected && typeof h.onCall === 'function') {
      actions.push(createCallButton(r, {
        canVideo: !!h.canVideo,
        onCall: (personId, mode, handle) => {
          h.onCall(personId, mode, handle);
          close();
        },
      }));
    }
    // ---- end calls ----
    const blockBtn = el('button', {
      type: 'button',
      class: 'btn btn-ghost btn-sm regular-block',
      text: t('Block'),
      disabled: h.connected ? null : '',
      title: t('Remove them from your regulars. They are not told.'),
      onClick: () => {
        const who = r.handle || 'this person';
        // A confirm, because it cannot be undone from here: the wave would have
        // to happen again, after another chat. The other person is told nothing.
        if (!window.confirm(`Block ${who}? You will no longer be regulars, and they cannot wave at you again. They are not told anything.`)) return;
        blockBtn.disabled = true;
        h.onBlock?.(r.personId);
      },
    });
    actions.push(blockBtn);
    return el('div', { class: `regular-row status-${status.kind}`, dataset: { personId: r.personId } }, [
      el('span', { class: 'regular-dot', 'aria-hidden': 'true' }),
      el('div', { class: 'regular-main' }, [
        el('div', { class: 'regular-name' }, [
          el('strong', { text: r.handle || 'Someone you talked to' }),
          status.text ? el('span', { class: 'regular-status', text: status.text }) : null,
        ].filter(Boolean)),
        el('span', { class: 'muted regular-since', text: formatSince(r.since) }),
      ]),
      el('div', { class: 'regular-actions' }, actions),
    ]);
  }

  function render() {
    listWrap.replaceChildren();
    const list = Array.isArray(state.regulars) ? state.regulars : [];
    if (!h.connected) {
      const n = Number(h.count) || 0;
      listWrap.append(
        el('p', {
          class: 'muted',
          text: n
            ? `You have ${n} regular${n === 1 ? '' : 's'}. Start a chat or open the rooms to see who is around.`
            : 'Start a chat or open the rooms to see your regulars.',
        }),
      );
    } else if (!list.length) {
      listWrap.append(
        el('div', { class: 'regulars-empty' }, [
          el('p', { text: t('No regulars yet.') }),
          el('p', { class: 'muted', text: t('When a chat ends you can wave. If the other person waves too, within seven days, you become regulars: you see each other here, and which public room the other is in. Nobody is told about a wave that was not returned.') }),
        ]),
      );
    } else {
      listWrap.append(el('div', { class: 'regulars-list' }, list.map(row)));
    }
    listWrap.append(recentChats());
    listWrap.append(invisibleSwitch());
    if (typeof h.onQuietHours === 'function') listWrap.append(quietHoursControl());
    // My community (ui/community.js): a node app.js built once, moved here on
    // every redraw. Inside the list wrap, so an open message thread hides it too.
    if (h.communityCard) listWrap.append(h.communityCard);
    if (h.connected && typeof h.onRefresh === 'function') {
      listWrap.append(el('div', { class: 'buy-row regulars-refresh' }, [el('button', { type: 'button', class: 'btn btn-ghost btn-sm', text: t('Refresh'), onClick: () => h.onRefresh() })]));
    }
  }

  render();
  document.body.append(modal);
  // A dialog behaves like one: a backdrop that dims and blocks the page,
  // role and aria-modal, focus moved in and trapped, Escape to leave, and
  // focus put back where it came from. See ui/modal.js.
  const dialog = asModal(modal, { label: 'Your regulars' });
  return {
    modal,
    api: {
      update(next) {
        if (!next) return;
        state = {
          regulars: Array.isArray(next.regulars) ? next.regulars : [],
          invisible: !!next.invisible,
          quietHours: next.quietHours || null,
          recent: Array.isArray(next.recent) ? next.recent : [],
        };
        render();
      },
      close,
      // The thread view's api (ui/messages.js), for app.js to route MSG,
      // MSG_LIST, MSG_DELETED, and the scoped notices to. Null when off.
      messages: thread ? thread.api : null,
    },
  };
}

export default { createBell, openRegulars, showNoticeToast, formatSince, regularStatus, waveResultText, WAVE_COPY, NO_IDENTITY_COPY, WAVE_OFFER_MS };
